import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { DataTable } from "../../components/DataTable";
import { FaExclamationTriangle, FaEye } from "react-icons/fa";
import {
  communityService,
  groupService,
  requestService,
  ServiceOnMount,
} from "../../services";
import { Skeleton } from "../../components/ui/skeleton";
import Modal from "../../components/Modal";
import { GoCheckCircleFill, GoXCircle, GoXCircleFill } from "react-icons/go";
import { GrGroup } from "react-icons/gr";
import ShortenedText from "../../components/ShortenedText";
import useUserStore2 from "../../stores/userStore2";
import LoadingImage from "../../components/LoadingImage";
import { toast } from "react-toastify";

const ViewCommunity = () => {
  const [viewGroupModal, setViewGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(null);
  const [isOpen, setIsOpen] = useState(null);

  const { communityId } = useParams();

  // const { communityId: userCommunityId } = useUserStore.getState();

  const { user } = useUserStore2((state) => state);

  const columns = [
    {
      accessorKey: "groupId",
      header: "Group ID",
      cell: ({ row }) => (
        <span>
          <ShortenedText text={row.getValue("groupId")} />
        </span>
      ),
    },
    {
      accessorKey: "groupName",
      header: "Group Name",
      cell: ({ row }) => <span>{row.getValue("groupName")}</span>,
    },
    {
      accessorKey: "totalMembers",
      header: "Total Members",
      cell: ({ row }) => <span>{row.getValue("totalMembers")}</span>,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => <span>{row.getValue("description")}</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const isMember = isGroupMember(row.original.groupId);
        return isMember ? (
          <span className="px-2 py-1 text-xs font-semibold text-green-600 bg-yellow-400 rounded-full">
            Joined
          </span>
        ) : null;
      },
    },
  ];

  const groupMap = (item) => ({
    groupId: item._id,
    groupName: item.name,
    totalMembers: item.total,
    description: item.description,
    dateCreated: item.create_date,
  });

  const getGroups = ServiceOnMount(
    () => groupService.getGroups(communityId),
    groupMap
  );

  // After fetching the groups, calculate the sum of totalMembers
  const totalMembersSum =
    getGroups?.data && getGroups.data.length > 0
      ? getGroups.data.reduce(
          (sum, group) => sum + (group.totalMembers || 0),
          0
        )
      : 0;

  const totalGroupsCount =
    getGroups?.data && getGroups.data.length > 0 ? getGroups.data.length : 0;

  const dataMap = (item) => ({
    communityID: item._id,
    name: item.name,
    status: item.status === "A" ? "Active" : "Inactive",
    description: item.description,
    createdDate: item.create_date
      ? new Date(item.create_date).toLocaleDateString()
      : "Not Available",
    totalGroups: item.totalGroups || "Not Available",
    totalMembers: item.totalMembers || "Not Available",
  });

  const getCommunityDetails = ServiceOnMount(
    () => communityService.getCommunityDetails(communityId),
    dataMap
  );
  const communityData = getCommunityDetails.data?.[0];

  const [activeTab, setActiveTab] = useState("general");

  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
  };

  const actions = (row) => [
    {
      label: "View Details",
      icon: <FaEye />,
      onClick: () => handleViewGroup(row),
      variant: "dropPlain",
    },
  ];

  const handleViewGroup = (group) => {
    setSelectedGroup(group);
    setViewGroupModal(true);
  };

  const handleSubmit = async () => {
    // const { userId, primaryProfileId, communityId, groupId } =
    //   useUserStore.getState(); // Get user data from Zustand

    // Request profile Id should be the current profile
    // Get group ID from user.currentProfile's memberships array and find membership with matching the communityId to get the group ID
    console.log(
      "user?.currentProfile?.memberships:",
      user?.currentProfile?.memberships
    );
    const matchingMembership = user?.currentProfile?.memberships?.find(
      (membership) => membership.communityId === communityId
    );
    console.log("matchingMembership:", matchingMembership);
    const groupId = matchingMembership?.groupId;

    const requestData = {
      RequestEntityId: user.id,
      RequestProfileId: user.currentProfile.id,
      TargetEntityId: user.id,
      TargetProfileId: user.currentProfile.id,
      CommunityId: communityId,
      GroupId: groupId,
      TransactionId: null, // Taken from selected row
      Type: "R", // 'Invitation', 'Suspension', 'Removal', 'Tx Review' so is I S R T
      Comment: "", // Empty for now
    };
    // console.log("Request Data:", requestData);

    try {
      setIsSubmitting(true);
      await requestService.addRequest(requestData);
      console.log("Request submitted successfully!", requestData);
      toast.success("🎉 Request Sent Successfully!", {
        autoClose: 2000,
      });
      setIsOpen(false); // Close modal on success
    } catch (error) {
      toast.error("❌ Failed to send request", {
        autoClose: 2000,
      });
      console.error("Failed to submit request:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCommunityMember = (communityId) => {
    // return if user?.currentProfile?.memberships? is undefined
    if (!user?.currentProfile?.memberships) return false;
    const matchingMembership = user?.currentProfile?.memberships?.find(
      (membership) => membership.communityId === communityId
    );
    return !!matchingMembership;
  };
  console.log("### communityId:", communityId);
  console.log(
    `### isCommunityMember(${communityId}):`,
    isCommunityMember(communityId)
  );
  console.log("### isCommunityMember('') no:", isCommunityMember(""));

  const isGroupMember = (groupId) => {
    if (!user?.currentProfile?.memberships) return false;
    return user.currentProfile.memberships.some(
      (membership) => membership.groupId === groupId
    );
  };

  return (
    <div className="flex flex-col md:flex-row min-h-screen ">
      <div className="flex-1 flex flex-col">
        <div className="p-8 flex flex-col">
          <div className="bg-white shadow p-4 pb-1 rounded-lg">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 p- w-full gap-4">
              {/* Left Section: Icon + Info */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 w-full sm:w-auto">
                <div className="bg-indigo-500 p-3 sm:p-4 rounded-full self-center sm:self-auto">
                  <GrGroup className="w-10 h-10 sm:w-16 sm:h-16 text-white" />
                </div>

                <div className="flex flex-col w-full">
                  <h1 className="text-lg font-semibold text-[#111827]">
                    {communityData ? (
                      communityData.name
                    ) : (
                      <Skeleton className="w-40 h-6" />
                    )}
                  </h1>

                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:space-x-8 gap-2">
                    <div>
                      <div className="text-sm font-semibold text-[#6B7280]">
                        Created Date
                      </div>
                      <div className="text-sm text-[#111827]">
                        {communityData ? (
                          communityData.createdDate
                        ) : (
                          <Skeleton className="w-40 h-6" />
                        )}
                      </div>
                    </div>

                    {isCommunityMember(communityData?.communityID) && (
                      // userCommunityId === communityData?.communityID
                      <div className="px-2 gap-1 flex py-1 items-center text-sm text-green-600 bg-yellow-400 rounded-full w-max">
                        <GoCheckCircleFill className="w-4 h-4" />
                        <span>You’re a Member</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Section: Button */}
              {isCommunityMember(communityData?.communityID) && (
                // userCommunityId === communityData?.communityID
                <div className="w-full sm:w-auto">
                  <Button
                    variant={"destructive"}
                    className="flex items-center gap-1 w-full sm:w-auto"
                    onClick={() => setIsOpen(true)}
                  >
                    <GoXCircle /> Removal Request
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Tabs Section */}
          <div className="flex">
            <button
              onClick={() => handleTabSwitch("general")}
              className={`relative mt-5 rounded-tl-xl px-3 pr-10 py-3 text-[14px] font-bold ${
                activeTab === "general"
                  ? "text-[#111827] bg-white"
                  : "text-[#14B8A6] bg-[#F0FDFA]"
              }`}
              style={{
                clipPath: "polygon(0 0, 70% 0, 100% 100%, 0% 100%)",
              }}
            >
              General Info
            </button>
          </div>

          {/* Details Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 md:p-6 mt-0 bg-white shadow rounded-r-lg rounded-b-lg">
            {activeTab === "general" && (
              <>
                <div>
                  <Label>Community Name</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {communityData ? (
                      communityData.name
                    ) : (
                      <Skeleton className="w-40 h-6" />
                    )}
                  </p>
                </div>

                <div>
                  <Label>Total Groups</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {getGroups.loading ? (
                      <Skeleton className="w-40 h-6" />
                    ) : (
                      totalGroupsCount
                    )}
                  </p>
                </div>

                <div>
                  <Label>Description</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {communityData ? (
                      communityData.description
                    ) : (
                      <Skeleton className="w-40 h-6" />
                    )}
                  </p>
                </div>

                <div>
                  <Label>Total Members</Label>
                  <p className="w-full p-2 border-b-2 border-gray-300 text-sm">
                    {getGroups.loading ? (
                      <Skeleton className="w-40 h-6" />
                    ) : (
                      totalMembersSum
                    )}
                  </p>
                </div>

                {getGroups?.data && getGroups.data.length > 0 && (
                  <div className="col-span-full border rounded-xl p-4">
                    <h1 className="font-bold text-xl mb-4">
                      Community Group List
                    </h1>
                    <hr />
                    <div className="overflow-x-auto w-full">
                      {getGroups.loading ? (
                        <LoadingImage />
                      ) : (
                        <DataTable
                          columns={columns}
                          data={getGroups.data}
                          actions={actions}
                          disableCheckboxes={true}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Submit Removal request Modal */}
        <Modal
          isOpen={isOpen}
          icon={<FaExclamationTriangle className="text-red-500" />}
          iconColor="red"
          onClose={() => setIsOpen(false)}
        >
          {/* Header - Centered */}
          <div className="text-center -mt-8">
            <h2 className="text-lg font-semibold text-gray-900">
              Removal Request
            </h2>
          </div>

          {/* Content - Centered Text */}
          <div className="space-y-3 text-sm text-center pt-4">
            Are you sure you wish to request to be removed from this group?
          </div>

          {/* Buttons - Centered */}
          <div className="flex justify-between gap-3 mt-6">
            <Button
              variant="outline"
              className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant={"destructive"}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Removing..." : "Remove"}
            </Button>
          </div>
        </Modal>

        {/* Group Details Modal */}
        <Modal
          isOpen={viewGroupModal}
          onClose={() => setViewGroupModal(false)}
          className=""
        >
          {/* Header */}
          <div className="flex justify-between items-center border-b pb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              Group Details
            </h2>
          </div>

          {/* Top Section: Group ID & Date Created */}
          {selectedGroup && (
            <div className="grid grid-cols-2 gap-4 py-4 text-sm">
              <div>
                <p className="text-gray-500">Group ID</p>
                <p className="font-medium text-gray-900">
                  <ShortenedText text={selectedGroup?.groupId} />
                </p>
              </div>
              <div>
                <p className="text-gray-500">Date Created</p>
                <p className="font-medium text-gray-900">
                  {selectedGroup?.dateCreated
                    ? new Date(selectedGroup.dateCreated).toLocaleDateString()
                    : "N/A"}
                </p>
                <p className="font-medium text-gray-900">
                  {selectedGroup?.dateCreated
                    ? new Date(selectedGroup.dateCreated).toLocaleTimeString()
                    : "N/A"}
                </p>
              </div>
            </div>
          )}

          {/* Group Information */}
          {selectedGroup && (
            <div className="space-y-3 text-sm border-t pt-4">
              {[
                { label: "Group Name", value: selectedGroup?.groupName },
                { label: "Total Members", value: selectedGroup?.totalMembers },
                { label: "Description", value: selectedGroup?.description },
              ].map((item, index) => (
                <div
                  key={index}
                  className={`flex text-left px-3 py-2 rounded ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <span className="text-gray-500 w-32">{item.label}</span>
                  <span className="text-gray-900 font-medium">
                    {item.value || "N/A"}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              className="py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              onClick={() => setViewGroupModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => setViewGroupModal(false)}>OK</Button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ViewCommunity;
